import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Book, Mic, Layout, BookOpen, LogOut, Video, LeafIcon, LucideWholeWord, LogIn, CalendarHeart, Speaker, MessageCircle, Award, Trophy, Puzzle, BrainCircuit, User, Quote, Users, Swords, Newspaper, StopCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Tooltip } from 'react-tooltip';
import Modal from './Modal';
import logo from '../assets/logo.png'

export default function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const [loginModal, setLoginModal] = useState(false);
  
  const isActive = (path: string) => location.pathname === path;

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };
  
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 py-2 md:pt-24 md:top-0 md:left-0 md:right-auto md:bottom-0 md:h-screen md:w-64 md:border-t-0 border-r">
      <Modal 
        isOpen={loginModal} 
        setIsModalOpen={setLoginModal} 
        title='Logout'
        message='Do you want to logout?'
        actionText='Logout'
        action={handleLogout}
        />
      <div className="flex md:flex-col md:h-full">

        <div className="flex w-full overflow-x-auto md:flex-col md:space-y-2 md:overflow-y-auto md:flex-1">
          <div className="flex flex-nowrap space-x-4 md:flex-col md:space-x-0 md:space-y-2 w-full px-2">
            <NavLink to="/dashboard" icon={Layout} label="Dashboard" isActive={isActive('/')} />
            <NavLink to="/news" icon={Newspaper} label="News" isActive={isActive('/news')} />
            <NavLink to="/vocabulary" icon={Book} label="Vocabulary" isActive={isActive('/vocabulary')} />
            <NavLink to="/expressions" icon={Quote} label="Expressions" isActive={isActive('/expressions')} />
            <NavLink to="/training" icon={BrainCircuit} label="Daily Training" isActive={isActive('/training')} />
            <NavLink to="/themes" icon={LeafIcon} label="Themes" isActive={isActive('/themes')} />
            <NavLink to="/speaking" icon={Mic} label="Speaking" isActive={isActive('/speaking')} />
            <NavLink to="/chat" icon={MessageCircle} label="AI Chat" isActive={isActive('/chat')} />
            <NavLink to="/translate" icon={LucideWholeWord} label="Translate" isActive={isActive('/translate')} />
            <NavLink to="/day" icon={CalendarHeart} label="Day Word" isActive={isActive('/day') || isActive('/activities')} />
            <NavLink to="/memory-game" icon={Puzzle} label="Jogo da Memória" isActive={isActive('/memory-game')} />
            <NavLink to="/crime-game" icon={BookOpen} label="Crime Game" isActive={isActive('/crime-game')} />
            <NavLink to="/stop-game" icon={StopCircle} label="STOP! Game" isActive={isActive('/stop-game')} />
            <NavLink to="/ranking" icon={Award} label="Ranking" isActive={isActive('/ranking')} />
            <NavLink to="/achievements" icon={Trophy} label="Achievements" isActive={isActive('/achievements')} />
            <NavLink to="/social" icon={Users} label="Friends" isActive={isActive('/social')} />
            <NavLink to="/challenges" icon={Swords} label="Challenges" isActive={isActive('/challenges')} />
            <NavLink to="/profile" icon={User} label="Profile" isActive={isActive('/profile')} />
          </div>
        </div>

        {currentUser ? (
          <div className="mt-auto mb-4 hidden md:block">
            <div className="px-4 md:py-3 md:border-t border-gray-200">
              <div className="flex items-center justify-between">
                <Link to="/profile" className='hidden md:block hover:text-indigo-600 transition-colors cursor-pointer truncate mr-2'>
                  <p className="text-sm font-medium text-gray-900 truncate">{currentUser.email}</p>
                </Link>
                <button
                  onClick={() => setLoginModal(true)}
                  className="md:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                >
                  <LogOut className="w-5 h-5" data-tooltip-id="logout" />
                  <Tooltip id="logout" place="top" content="logout" />
                </button>
              </div>
            </div>
          </div>
        ) : (<div className="mt-auto mb-4 hidden md:block">
          <div className="px-4 md:border-t border-gray-200 cursor-pointer rounded-sm hover:shadow-sm" onClick={() => navigate('/login')}>
            <div className="flex items-center justify-between">
              <div className='hidden md:block'>
                <p className="text-sm font-medium text-gray-900">Login</p>
              </div>
              <button
                onClick={() => navigate('/login')}
                className="md:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <LogIn className="w-5 h-5" data-tooltip-id="login" />
                <Tooltip id="login" place="top" content="login" />
              </button>
            </div>
          </div>
        </div>)}
      </div>
    </nav>
  );
}

interface NavLinkProps {
  to: string;
  icon: React.ElementType;
  label: string;
  isActive: boolean;
}

function NavLink({ to, icon: Icon, label, isActive }: NavLinkProps) {
  return (
    <Link
      to={to}
      className={`flex items-center p-3 rounded-lg transition-colors ${
        isActive
          ? 'text-indigo-600 bg-indigo-50'
          : 'text-gray-600 hover:bg-gray-50'
      }`}
    >
      <Icon className="w-6 h-6" />
      <span className="ml-3 hidden md:block">{label}</span>
    </Link>
  );
}